import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  Observable,
  FetchResult,
} from '@apollo/client';
import axios from 'axios';
import {GraphQLError} from 'graphql';
import React, {
  useContext,
  useCallback,
  useState,
  PropsWithChildren,
} from 'react';

import {
  localStorageRefreshTokenKey,
  localStorageAccessTokenKey,
  apiUrl,
  baseUrl,
} from './constants';
import {
  RefreshTokenMutation,
  RefreshTokenMutationVariables,
  RefreshTokenDocument,
} from '../generated';

interface NewTokens {
  newAccessToken: string;
  newRefreshToken: string;
}
export interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: {
    countryCode: string;
    number: string;
  };
  createdAt: string;
  emailVerifiedAt: string | null;
}

interface SignInResponse {
  accessToken: string;
  refreshToken: string;
  user: UserData;
}

interface GraphQLResponse<T> {
  data?: {
    [key: string]: T | undefined;
  };
  errors?: GraphQLError[];
}

interface Context {
  isAuthenticated: boolean;
  logout(): Promise<void>;
  makeLoginWithPost(email: string, password: string): Promise<void>;
  loginError?: string;
  getNewTokens(): Promise<NewTokens | undefined>;
}

export const AuthContext = React.createContext<Context>({
  isAuthenticated: false,
  logout: async () => undefined,
  makeLoginWithPost: async () => undefined,
  getNewTokens: async () => undefined,
});

export const useAuth = (): Context => useContext(AuthContext);
export const promiseToObservable = (
  promise: Promise<NewTokens | undefined>
): Observable<unknown> =>
  new Observable((subscriber) => {
    promise.then(
      (value) => {
        if (subscriber.closed) return;
        subscriber.next(value);
        subscriber.complete();
      },
      (err) => subscriber.error(err)
    );
  });

const AuthProvider = ({children}: PropsWithChildren) => {
  const [, setAccessToken] = useState<string | null | undefined>();
  const [, setRefreshToken] = useState<string | null | undefined>();
  const [erroLogin, setErrorLogin] = useState('');

  // Refresh tokens
  const getNewTokens = async () => {
    const httpLink = createHttpLink({
      uri: apiUrl,
    });
    const client = new ApolloClient({
      cache: new InMemoryCache(),
      link: httpLink,
    });
    const localStorageRefreshToken = localStorage.getItem(
      localStorageRefreshTokenKey
    );
    if (!localStorageRefreshToken) return;
    let res: FetchResult<RefreshTokenMutation, Record<string, unknown>>;
    try {
      res = await client.mutate<
        RefreshTokenMutation,
        RefreshTokenMutationVariables
      >({
        mutation: RefreshTokenDocument,
        variables: {
          input: {
            refreshToken: localStorageRefreshToken,
          },
        },
      });
    } catch {
      await logout();
      return;
    }

    const newAccessToken = res.data?.refreshToken?.accessToken;
    const newRefreshToken = res.data?.refreshToken?.refreshToken;
    if (newAccessToken && newRefreshToken) {
      setTokens(newAccessToken, newRefreshToken);
      return {newAccessToken, newRefreshToken};
    }
  };

  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem(localStorageAccessTokenKey)
  );

  // Utiliity
  const setTokens = useCallback((accessToken: string, refreshToken: string) => {
    localStorage.setItem(localStorageAccessTokenKey, accessToken);
    localStorage.setItem(localStorageRefreshTokenKey, refreshToken);
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
    setIsAuthenticated(true);
  }, []);

  const removeTokens = useCallback(() => {
    localStorage.removeItem(localStorageAccessTokenKey);
    localStorage.removeItem(localStorageRefreshTokenKey);
    setAccessToken(undefined);
    setRefreshToken(undefined);
    setIsAuthenticated(false);
  }, []);

  // Functions
  const logout = useCallback(async (): Promise<void> => {
    removeTokens();
  }, [removeTokens]);

  const makeLoginWithPost = async (email: string, password: string) => {
    const api = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const response = await api.post<GraphQLResponse<SignInResponse>>('/query', {
      query: `
      mutation Login($input: LoginInput!) {
        login(input: $input) {
          accessToken
          refreshToken
          user {
            emailVerifiedAt
            createdAt
          }
        }
      }
    `,
      variables: {
        input: {
          password,
          email,
        },
      },
    });

    if (response.data.errors && response.data.errors.length > 0) {
      setErrorLogin(`Unable to log in, please try again later`);
    }

    if (!response.data.data?.login) {
      throw new Error('Login data is missing from the response');
    }

    const {accessToken, refreshToken} = response.data.data.login;
    setTokens(accessToken, refreshToken);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        logout,
        makeLoginWithPost,
        getNewTokens,
        loginError: erroLogin,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
